import React, { useState, useEffect } from 'react';
import AlertsView from './AlertsView';
import { api } from '../services/api';
import { Box, CircularProgress, Alert } from '@mui/material';
import { DEFAULT_PARENT_LATENCY_ALERT_MS } from '../services/latency';

const STORAGE_KEY = 'nms_alerts_thresholds';

const loadThresholds = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) return JSON.parse(raw);
  } catch { /* ignore corrupt data */ }
  return null;
};

const AlertsPage = ({ isActive = true }) => {
  const [devices, setDevices] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [healthThreshold, setHealthThreshold] = useState(() => loadThresholds()?.health ?? 0.7);
  const [txFailedThreshold, setTxFailedThreshold] = useState(() => loadThresholds()?.txFailed ?? 0.3);
  const [latencyThresholdMs, setLatencyThresholdMs] = useState(() => loadThresholds()?.latency ?? DEFAULT_PARENT_LATENCY_ALERT_MS);

  // Persist all thresholds whenever any one changes — avoids stale-closure issues.
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ health: healthThreshold, txFailed: txFailedThreshold, latency: latencyThresholdMs }));
    } catch { /* quota */ }
  }, [healthThreshold, txFailedThreshold, latencyThresholdMs]);

  const fetchDevices = async () => {
    try {
      setIsLoading(true);
      setError(null);
      console.log('Fetching devices for alerts...');
      const devicesResponse = await api.getDevices({
        fields: ['core', 'radios', 'traffic_control'],
      });
      // Handle new response format with devices array
      const devicesData = devicesResponse?.devices || (Array.isArray(devicesResponse) ? devicesResponse : []);
      console.log('Devices received for alerts:', devicesData);
      setDevices(devicesData || []);
    } catch (err) {
      console.error('Error fetching devices for alerts:', err);
      setError(err.message || 'Failed to fetch devices');
      setDevices([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!isActive) return;

    fetchDevices();

    // Set up polling to refresh device data every 30 seconds (only when tab is active)
    const interval = setInterval(fetchDevices, 30000);

    return () => clearInterval(interval);
  }, [isActive]);

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          Error loading devices: {error}
        </Alert>
      </Box>
    );
  }

  if (isLoading && devices.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <AlertsView
      devices={devices}
      isLoading={isLoading}
      healthThreshold={healthThreshold}
      setHealthThreshold={setHealthThreshold}
      txFailedThreshold={txFailedThreshold}
      setTxFailedThreshold={setTxFailedThreshold}
      latencyThresholdMs={latencyThresholdMs}
      setLatencyThresholdMs={setLatencyThresholdMs}
    />
  );
};

export default AlertsPage; 